import { Link, useParams } from "@tanstack/react-router";
import {
  ArrowRightLeft,
  CalendarClock,
  GitBranch,
  Settings,
  ShieldCheck,
  SlidersHorizontal,
  Users,
} from "lucide-react";
import { Header } from "@/app/layout/Header";
import { Main } from "@/app/layout/Main";
import { canUseMerchantPermission } from "@/modules/merchant/presentation/api/client";
import { hasShopFeature } from "@/modules/merchant/presentation/lib/has-shop-feature";
import { shopBreadcrumbs } from "@/modules/merchant/presentation/lib/shop-breadcrumb";
import { useMerchantShops } from "@/modules/merchant/presentation/store/use-merchant-shops";
import { ShopExchangeRatesSection } from "@/modules/merchant/presentation/ui/ShopExchangeRatesSection";
import { ShopSettingsFormSection } from "@/modules/merchant/presentation/ui/ShopSettingsFormSection";
import { MerchantPermissions } from "@/modules/roles/domain/contracts/merchant-permissions";
import { Breadcrumb } from "@/shared/ui/Breadcrumb";

const ANCHOR_GENERAL = "shop-settings-general";
const ANCHOR_FX = "shop-settings-fx";
const BRANCHES_ROUTE = "/app/shops/$shopId/branches" as const;
const STAFF_ROUTE = "/app/shops/$shopId/staff" as const;
const ROLES_ROUTE = "/app/shops/$shopId/roles" as const;
const SHIFTS_ROUTE = "/app/shops/$shopId/shifts/calendar" as const;

/** ສະກຸນ, VAT, MC2 + ອັດຕາແລກ MC3 — ສິດລະຫວ່າງສ່ວນຕາມ component ຍ່ອຍ */
export function ShopSettingsPage() {
  const params = useParams({ strict: false });
  const shopId = params?.shopId as string | undefined;
  const { currentShop } = useMerchantShops(shopId);
  const permissions = currentShop?.permissions ?? [];
  const verifiedLevel = currentShop?.verifiedLevel ?? null;
  const canReadStaff = canUseMerchantPermission(
    permissions,
    MerchantPermissions.shop_staff.read,
    verifiedLevel,
  );
  const canReadRoles = canUseMerchantPermission(
    permissions,
    MerchantPermissions.shop_roles.read,
    verifiedLevel,
  );
  const canReadShifts = canUseMerchantPermission(
    permissions,
    MerchantPermissions.shifts.read,
    verifiedLevel,
  );
  const showBranches =
    shopId && canReadStaff && hasShopFeature(currentShop?.featureFlags, "multi_branch");
  const showShifts =
    shopId && canReadShifts && hasShopFeature(currentShop?.featureFlags, "shifts");

  return (
    <>
      <Header>
        <Breadcrumb items={shopBreadcrumbs.settings(shopId)} />
      </Header>
      <Main>
        <div className="mx-auto max-w-4xl">
          <section
            className="relative mb-8 overflow-hidden rounded-2xl border border-border/80 bg-linear-to-br from-primary/[0.07] via-background to-muted/40 p-6 shadow-sm sm:p-8"
            aria-labelledby="shop-settings-title"
          >
            <div className="pointer-events-none absolute -right-12 -top-20 size-44 rounded-full bg-primary/10 blur-3xl" />
            <div className="relative space-y-3">
              <div className="inline-flex items-center gap-2 rounded-full border border-border/60 bg-background/80 px-3 py-1 text-muted-foreground text-xs shadow-sm backdrop-blur-sm">
                <Settings className="size-3.5 text-primary" aria-hidden />
                <span>ຕັ້ງຄ່າ</span>
              </div>
              <h1
                id="shop-settings-title"
                className="font-heading font-semibold text-2xl text-foreground tracking-tight sm:text-3xl"
              >
                ຕັ້ງຄ່າຮ້ານ
              </h1>
              <p className="max-w-2xl text-muted-foreground text-sm leading-relaxed">
                {currentShop?.shopName ? (
                  <span className="text-foreground font-medium">
                    {currentShop.shopName}
                  </span>
                ) : null}
                {currentShop?.shopName ? " — " : null}
                ສະກຸນເລີ່ມຕົ້ນ, VAT, MC2 ແລະ ອັດຕາແລກປ່ຽນ (MC3) ສຳລັບ POS ແລະ
                ລາຍງານ.
              </p>
              <nav
                className="flex flex-wrap gap-2 pt-2"
                aria-label="ລິ້ງໄປພາກໃນໜ້າ"
              >
                <a
                  href={`#${ANCHOR_GENERAL}`}
                  className="inline-flex items-center gap-1.5 rounded-lg border border-border/80 bg-background/90 px-3 py-1.5 text-foreground text-xs font-medium shadow-sm transition-colors hover:border-primary/40 hover:bg-primary/5"
                >
                  <SlidersHorizontal className="size-3.5 text-primary" />
                  ທົ່ວໄປ
                </a>
                <a
                  href={`#${ANCHOR_FX}`}
                  className="inline-flex items-center gap-1.5 rounded-lg border border-border/80 bg-background/90 px-3 py-1.5 text-foreground text-xs font-medium shadow-sm transition-colors hover:border-primary/40 hover:bg-primary/5"
                >
                  <ArrowRightLeft className="size-3.5 text-primary" />
                  ອັດຕາແລກ
                </a>
                {showBranches ? (
                  <Link
                    to={BRANCHES_ROUTE}
                    params={{ shopId }}
                    className="inline-flex items-center gap-1.5 rounded-lg border border-border/80 bg-background/90 px-3 py-1.5 font-medium text-foreground text-xs shadow-sm transition-colors hover:border-primary/40 hover:bg-primary/5"
                  >
                    <GitBranch className="size-3.5 text-primary" />
                    ສາຂາ
                  </Link>
                ) : null}
                {shopId && canReadStaff ? (
                  <Link
                    to={STAFF_ROUTE}
                    params={{ shopId }}
                    className="inline-flex items-center gap-1.5 rounded-lg border border-border/80 bg-background/90 px-3 py-1.5 font-medium text-foreground text-xs shadow-sm transition-colors hover:border-primary/40 hover:bg-primary/5"
                  >
                    <Users className="size-3.5 text-primary" />
                    ພະນັກງານ
                  </Link>
                ) : null}
                {shopId && canReadRoles ? (
                  <Link
                    to={ROLES_ROUTE}
                    params={{ shopId }}
                    className="inline-flex items-center gap-1.5 rounded-lg border border-border/80 bg-background/90 px-3 py-1.5 font-medium text-foreground text-xs shadow-sm transition-colors hover:border-primary/40 hover:bg-primary/5"
                  >
                    <ShieldCheck className="size-3.5 text-primary" />
                    ບົດບາດ
                  </Link>
                ) : null}
                {showShifts ? (
                  <Link
                    to={SHIFTS_ROUTE}
                    params={{ shopId }}
                    className="inline-flex items-center gap-1.5 rounded-lg border border-border/80 bg-background/90 px-3 py-1.5 font-medium text-foreground text-xs shadow-sm transition-colors hover:border-primary/40 hover:bg-primary/5"
                  >
                    <CalendarClock className="size-3.5 text-primary" />
                    ກະພະນັກງານ
                  </Link>
                ) : null}
              </nav>
            </div>
          </section>

          <div className="space-y-8 pb-4">
            <ShopSettingsFormSection shopId={shopId} sectionId={ANCHOR_GENERAL} />
            <ShopExchangeRatesSection shopId={shopId} sectionId={ANCHOR_FX} />
          </div>
        </div>
      </Main>
    </>
  );
}
